import { type InvestigationProgressEventType } from "@validereinc/domain";
import { timeFormatter } from "@validereinc/utilities";
import classNames from "classnames/bind";
import parseISO from "date-fns/parseISO";
import React, { useMemo } from "react";
import { formatInvestigationProgressDisplayText } from "#src/routes/investigations/[investigationId]/progressDisplay.helpers";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

const PRIMARY_FIELDS: ReadonlyArray<keyof InvestigationProgressEventType> = [
  "v",
  "type",
  "ts",
  "phase",
  "detail",
];

export type ProgressFeedProps = {
  progress: InvestigationProgressEventType[];
};

type RenderableProgressEntryType = {
  key: string;
  phase: string;
  detail: string;
  ts: string;
  meta: Record<string, unknown>;
};

const UNKNOWN_PHASE_LABEL = "Working";

const formatProgressTimestamp = (ts: string): string =>
  timeFormatter(parseISO(ts));

const toRenderableEntry = (
  event: InvestigationProgressEventType,
  index: number
): RenderableProgressEntryType => {
  const meta: Record<string, unknown> = {};
  Object.entries(event).forEach(([key, value]) => {
    if (PRIMARY_FIELDS.includes(key as keyof InvestigationProgressEventType)) {
      return;
    }
    meta[key] = value;
  });

  const rawPhase =
    typeof event.phase === "string" && event.phase.length > 0
      ? event.phase
      : UNKNOWN_PHASE_LABEL;
  const rawDetail = typeof event.detail === "string" ? event.detail : "";

  return {
    key: `${event.ts}-${index}`,
    phase: formatInvestigationProgressDisplayText(rawPhase),
    detail: formatInvestigationProgressDisplayText(rawDetail),
    ts: event.ts,
    meta,
  };
};

export const ProgressFeed = ({ progress }: ProgressFeedProps) => {
  const reversedEntries = useMemo<RenderableProgressEntryType[]>(
    () =>
      [...progress]
        .map((event, index) => toRenderableEntry(event, index))
        .reverse(),
    [progress]
  );

  if (reversedEntries.length === 0) {
    return (
      <div
        className={cx("progress-feed")}
        data-testid="progress-feed"
      >
        <div className={cx("progress-feed__entry")}>
          <span
            className={cx("progress-feed__dot", "progress-feed__dot--latest")}
          />
          <div className={cx("progress-feed__body")}>
            <div className={cx("progress-feed__phase")}>Starting</div>
            <div className={cx("progress-feed__detail")}>
              Waiting for the agent to begin...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx("progress-feed")}
      data-testid="progress-feed"
    >
      {reversedEntries.map((entry, index) => {
        const isLatest = index === 0;
        const hasMeta = Object.keys(entry.meta).length > 0;

        return (
          <div
            key={entry.key}
            className={cx("progress-feed__entry")}
          >
            <span
              className={cx("progress-feed__dot", {
                "progress-feed__dot--latest": isLatest,
              })}
            />
            <div className={cx("progress-feed__body")}>
              <div className={cx("progress-feed__phase")}>
                {entry.phase}
                <span className={cx("progress-feed__timestamp")}>
                  {formatProgressTimestamp(entry.ts)}
                </span>
              </div>
              {entry.detail ? (
                <div className={cx("progress-feed__detail")}>
                  {entry.detail}
                </div>
              ) : null}
              {hasMeta ? (
                <details className={cx("progress-feed__meta")}>
                  <summary className={cx("progress-feed__meta-summary")}>
                    Show details
                  </summary>
                  <pre className={cx("progress-feed__meta-content")}>
                    {JSON.stringify(entry.meta, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};
